/*
Google Tag/Analytics Resources
https://developers.google.com/gtagjs/reference/api
https://developers.google.com/tag-platform/gtagjs/reference

Code below is a hand-wavy homage to https://github.com/codler/react-ga4/blob/master/src/ga4.js


How it works:
    Both GA and GTM work by populating window.dataLayer[] with events and "commands" via gtag()
    gtag(xxx) just passes ...args into a new dataLayer list item.

    To provide an event, dataLayer would have a new item that looks like:
        ["event", "$eventName", {"dataKey1": "$dataKey1Val", "dataKey2": "$dataKey2Val"}]

    To provide user properties
        it expects ["set", "user_id", "$userId"] for the GA managed user_id variable,
        it expects ["set", "user_properties", {"user_prop_1": "$user_prop_1_val", ...}] to set other custom user props

    user properties are configured within google analytics
        https://support.google.com/analytics/answer/14239618?sjid=15197555999506085801-NC#zippy=%2Canalyze-the-custom-dimension-in-a-report%2Canalyze-the-custom-dimension-in-an-exploration%2Cbuild-an-audience-using-the-dimension
    user events are configured via GTM, which pass data into GA
        Explore the Login Event tag here: https://tagmanager.google.com/#/container/accounts/6219743451/containers/178922551/workspaces/30/tags
*/

declare global {
  interface Window {
    gtag: any;
    dataLayer: any;
  }
}

const MEASUREMENT_URL = 'https://www.googletagmanager.com/gtag/js';

export enum EventTypes {
  CLIENT_VIDEO_PLAY = 'client_video_play',
  COMPLETED_ONBOARDING = 'completed_onboarding',
  LOGIN = 'login',
  LOGOUT = 'logout',
  PLAY_EXPERIENCE = 'play_experience',
  SCHEDULE_CALL = 'schedule_call',
  SET_TEST_USER = 'set_test_user',
  SET_USER_DATA = 'set_user_data',
  SET_USER_ID = 'set_user_id',
  SIGN_UP = 'sign_up',
  STARTED_ONBOARDING = 'started_onboarding',
  SUBMIT_QUERY = 'submit_query',
  VISIT_RESOURCE = 'visit_resource',
}

class Analytics {
  private loaded: boolean;
  private tagId: string | null;

  private sentUserProperties: string[] = [];

  constructor() {
    this.loaded = false;
    this.tagId = null;
    this.resetSentParams();
  }

  send(...args: any[]) {
    if (this.loaded) {
      window.gtag(args);
    }
  }

  resetSentParams() {
    this.sentUserProperties = [];
  }

  init(tagId: string) {
    this.tagId = tagId;
    this.loadScript();
  }

  loadScript() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    if (!this.tagId) {
      console.error('Analytics initialized with no gtag id');
      return;
    }
    if (!this.loaded) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `${MEASUREMENT_URL}?id=${this.tagId}`;
      document.body.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };

      this.loaded = true;
      window.gtag('js', new Date());
      window.gtag('config', this.tagId);
    }
  }

  trackEvent(event: EventTypes, ...args: any[]) {
    // google analytics events can fail and prevent other javascript from running, so we wrap in a try/catch and eat the error
    try {
      switch (event) {
        case EventTypes.CLIENT_VIDEO_PLAY:
          window.gtag('event', event, { client_name: args[0] });
          break;
        case EventTypes.COMPLETED_ONBOARDING:
          window.gtag('event', event, { client_name: args[0], gender: args[1], age: args[2] });
          break;
        case EventTypes.LOGIN:
          window.gtag('event', event, { method: args[0] });
          break;
        case EventTypes.LOGOUT:
          this.resetSentParams();
          break;
        case EventTypes.PLAY_EXPERIENCE:
          window.gtag('event', event, { experience_id: args[0], media_type: args[1], playback_location: args[2] });
          break;
        case EventTypes.SCHEDULE_CALL:
          window.gtag('event', event, { call_type: args[0], call_topics: args[1] });
          break;
        case EventTypes.SET_USER_DATA:
          if (!this.sentUserProperties.includes(EventTypes.SET_USER_DATA)) {
            window.gtag('set', 'user_properties', {
              member_group_id: args[0],
              test_user: args[1],
              client_code: args[2],
              gender: args[3],
              age: args[4],
            });
            this.sentUserProperties.push(EventTypes.SET_USER_DATA);
          }
          break;
        case EventTypes.SET_USER_ID:
          if (!this.sentUserProperties.includes(EventTypes.SET_USER_ID)) {
            window.gtag('set', 'user_id', args[0]);
            this.sentUserProperties.push(EventTypes.SET_USER_ID);
          }
          break;
        case EventTypes.SIGN_UP:
          window.gtag('event', event);
          break;
        case EventTypes.STARTED_ONBOARDING:
          window.gtag('event', event, { client_name: args[0], gender: args[1], age: args[2] });
          break;
        case EventTypes.SUBMIT_QUERY:
          window.gtag('event', event);
          break;
        case EventTypes.VISIT_RESOURCE:
          window.gtag('event', event, { resource_id: args[0] });
          break;
      }
    } catch (error) {
      console.log('Failed to send events to Google Analytics', error);
    }
  }
}

export const analytics = new Analytics();
